/* eslint-disable prettier/prettier */
/*
 * @description 章节批注列表（作为 Popup / Bubble 的 children 使用）
 *  - 浅色主题
 *  - 数据为扁平结构，通过 type 区分「章节(chapter)」与「批注(annotation)」
 *  - 连续的 annotation 归属到它前面最近的那个 chapter
 *  - 章节带蓝色序号徽标，批注挂在时间轴上
 */
import { memo, useCallback, useMemo } from 'react';
import './mobile-replay.css';

/** 列表项类型：章节 / 章节中的批注 */
export type ChapterListItemType = 'chapter' | 'annotation';

/** 扁平列表项 */
export interface ChapterListItem {
  /** 展示文案 */
  text: string;
  /** 时间（秒） */
  time: number;
  /** 类型：chapter=章节，annotation=章节中的批注 */
  type: ChapterListItemType;
}

export interface MobileReplayChaptersProps {
  /** 弹层标题，默认 "章节批注" */
  title?: string;
  /** 扁平数据列表 */
  list?: ChapterListItem[];
  /** 当前 audio 播放位置（秒），用于高亮正在播放的条目 */
  currentTime?: number;
  /** 关闭按钮回调 */
  onClose?: () => void;
  /** 点击条目回调，index 为该条目在原始 list 中的下标 */
  onItemClick?: (item: ChapterListItem, index: number) => void;
  /** 是否展示右上角关闭按钮，默认 false */
  showClose?: boolean;
  /** 自定义类名 */
  className?: string;
}

/** 带原始下标的条目 */
interface IndexedItem {
  item: ChapterListItem;
  index: number;
}

/** 章节区块：一个章节头 + 其下的批注集合 */
interface ChapterSection {
  key: string;
  /** 章节头（可能为空：列表开头出现了未归属的批注） */
  chapter?: IndexedItem;
  /** 章节序号（从 1 开始），仅 chapter 存在时有值 */
  chapterNo?: number;
  /** 该章节下的批注 */
  annotations: IndexedItem[];
}

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M5 5L15 15M15 5L5 15"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 把秒数格式化为 HH:MM:SS */
const formatTime = (sec: number): string => {
  const total = Number.isFinite(sec) && sec > 0 ? Math.floor(sec) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

/** 把扁平列表归并成「章节 -> 批注」的区块结构 */
const buildSections = (list: ChapterListItem[]): ChapterSection[] => {
  const sections: ChapterSection[] = [];
  let chapterNo = 0;
  let current: ChapterSection | null = null;
  // 出现在第一个章节之前的批注(如 time 0 的 Intro)先缓存，等第一个章节出现后并入它
  const leading: IndexedItem[] = [];

  list.forEach((item, index) => {
    if (item.type === 'chapter') {
      chapterNo += 1;
      current = {
        key: `chapter-${index}`,
        chapter: { item, index },
        chapterNo,
        annotations: [],
      };
      // 第一个章节：把它之前的松散批注并到该章节下(排在其它批注前面)
      if (leading.length > 0) {
        current.annotations.push(...leading);
        leading.length = 0;
      }
      sections.push(current);
    } else if (!current) {
      // 还没有任何章节：先缓存，等第一个章节出现后并入它
      leading.push({ item, index });
    } else {
      // 批注：归到当前章节
      current.annotations.push({ item, index });
    }
  });

  // 整个列表都没有章节：把缓存的批注作为无章节头的松散区块展示
  if (leading.length > 0) {
    sections.unshift({ key: 'loose', annotations: leading });
  }

  return sections;
};

const MobileReplayChapters = memo((props: MobileReplayChaptersProps) => {
  const { title = '章节批注', list = [], currentTime = 0, onClose, onItemClick, showClose = false, className } = props;

  const sections = useMemo(() => buildSections(list), [list]);

  /** 当前正在播放的条目下标：最后一个 time <= currentTime 的项 */
  const activeIndex = useMemo(() => {
    let idx = -1;
    list.forEach((item, i) => {
      if (item.time <= currentTime) idx = i;
    });
    return idx;
  }, [list, currentTime]);

  const handleItemClick = useCallback(
    (item: ChapterListItem, index: number) => {
      onItemClick?.(item, index);
    },
    [onItemClick],
  );

  return (
    <div className={`chapter-list ${className || ''}`.trim()}>
      <div className="chapter-list__header">
        <h2 className="chapter-list__title">{title}</h2>
        {showClose && (
          <button type="button" className="chapter-list__close" onClick={onClose} aria-label="close">
            <CloseIcon />
          </button>
        )}
      </div>

      <div className="chapter-list__body">
        {sections.map((section) => {
          // 提到 map 顶部，TS 类型缩窄后即可在闭包中安全使用，无需非空断言
          const { chapter } = section;
          return (
            <div key={section.key} className="chapter-list__section">
              {chapter && (
                <div
                  className={`chapter-list__chapter ${chapter.index === activeIndex ? 'chapter-list__chapter--active' : ''
                    }`.trim()}
                  onClick={() => handleItemClick(chapter.item, chapter.index)}
                  role="button"
                  tabIndex={0}
                >
                  <span className="chapter-list__badge">{section.chapterNo}</span>
                  <span className="chapter-list__chapter-title">{chapter.item.text}</span>
                </div>
              )}

              {section.annotations.length > 0 && (
                <div className="chapter-list__annotations">
                  {section.annotations.map(({ item, index }) => (
                    <div
                      key={index}
                      className={`chapter-list__annotation ${index === activeIndex ? 'chapter-list__annotation--active' : ''
                        }`.trim()}
                      onClick={() => handleItemClick(item, index)}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="chapter-list__timeline" aria-hidden="true">
                        <span className="chapter-list__dot" />
                      </span>
                      <span className="chapter-list__time">{formatTime(item.time)}</span>
                      <span className="chapter-list__text">{item.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

MobileReplayChapters.displayName = 'MobileReplayChapters';

export default MobileReplayChapters;
